import React, { useState, useEffect, useRef } from 'react';
import './Assignments.css';
import Navbar from '../../Navbar/Navbar';

const LOGIN_API_URL = 'https://lfsofficial.in/api/admin_login.php';
const UPLOAD_API_URL = 'https://lfsofficial.in/api/admin_assignments_upload.php';
const LIST_API_URL = 'https://lfsofficial.in/api/admin_assignments_list.php';
const DELETE_API_URL = 'https://lfsofficial.in/api/admin_assignments_delete.php';

const CLASS_OPTIONS = [
  'All Classes', 'Class I', 'Class II', 'Class III', 'Class IV', 'Class V',
  'Class VI', 'Class VII', 'Class VIII', 'Class IX', 'Class X',
];

const SUBJECT_OPTIONS = [
  'All Subjects', 'Mathematics', 'Science', 'English', 'Hindi', 'Social Science',
  'Physics', 'Chemistry', 'Biology', 'History', 'Geography', 'Computer Science', 'Sanskrit',
];

const Assignments = () => {
  const [assignments, setAssignments] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState('');

  const [filterClass, setFilterClass] = useState('All Classes');
  const [filterSubject, setFilterSubject] = useState('All Subjects');
  const [searchQuery, setSearchQuery] = useState('');

  // Admin state
  const [showAdminModal, setShowAdminModal] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loginUser, setLoginUser] = useState('');
  const [loginPass, setLoginPass] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);

  // Upload form
  const [form, setForm] = useState({ title: '', subject: '', classLevel: '', dueDate: '', description: '' });
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadError, setUploadError] = useState('');
  const [uploadSuccess, setUploadSuccess] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileRef = useRef();

  // Delete confirm
  const [deleteId, setDeleteId] = useState(null);

  // ── fetch assignments from the database ──
  const fetchAssignments = async () => {
    setListLoading(true);
    setListError('');
    try {
      const response = await fetch(LIST_API_URL);
      const data = await response.json();
      if (data.success) {
        setAssignments(data.assignments);
      } else {
        setListError('Could not load assignments.');
      }
    } catch (err) {
      setListError(`Unable to reach the server: ${err.message}`);
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    fetchAssignments();
  }, []);

  // ── filtered list ──
  const filtered = assignments.filter((a) => {
    const matchClass = filterClass === 'All Classes' || a.class_level === filterClass;
    const matchSubj = filterSubject === 'All Subjects' || a.subject === filterSubject;
    const matchSearch =
      a.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      a.subject.toLowerCase().includes(searchQuery.toLowerCase());
    return matchClass && matchSubj && matchSearch;
  });

  // ── login (real, server-verified) ──
  const handleLogin = async () => {
    setLoginError('');
    if (!loginUser || !loginPass) {
      setLoginError('Please enter both username and password.');
      return;
    }

    setLoginLoading(true);
    try {
      const response = await fetch(LOGIN_API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: loginUser, password: loginPass }),
      });
      const data = await response.json();

      if (data.success) {
        setIsLoggedIn(true);
        setLoginError('');
      } else {
        setLoginError(data.message || 'Invalid username or password.');
      }
    } catch (err) {
      setLoginError(`Unable to reach the server: ${err.message}`);
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    // Keep loginUser/loginPass cleared so nothing sensitive lingers in memory
    setLoginUser('');
    setLoginPass('');
    setShowAdminModal(false);
    setForm({ title: '', subject: '', classLevel: '', dueDate: '', description: '' });
    setUploadedFile(null);
    setUploadError('');
    setUploadSuccess('');
  };

  // ── file handling (client-side selection, before upload) ──
  const handleFileDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) validateFile(file);
  };

  const handleFileInput = (e) => {
    const file = e.target.files[0];
    if (file) validateFile(file);
  };

  const validateFile = (file) => {
    const allowed = [
      'application/pdf', 'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'image/jpeg', 'image/png',
    ];
    if (!allowed.includes(file.type)) {
      setUploadError('Only PDF, DOC, DOCX, PPT, PPTX, JPG, PNG files allowed.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setUploadError('File size must be under 10 MB.');
      return;
    }
    setUploadError('');
    setUploadedFile(file);
  };

  // Icon while still choosing a file (real File object, has .type)
  const getFileIcon = (type) => {
    if (!type) return '📄';
    if (type.includes('pdf')) return '📕';
    if (type.includes('word')) return '📘';
    if (type.includes('presentation') || type.includes('powerpoint')) return '📙';
    if (type.includes('image')) return '🖼️';
    return '📄';
  };

  // Icon for already-uploaded assignments (server only gives us the filename)
  const getFileIconByName = (filename) => {
    if (!filename) return '📄';
    const ext = filename.split('.').pop().toLowerCase();
    if (ext === 'pdf') return '📕';
    if (ext === 'doc' || ext === 'docx') return '📘';
    if (ext === 'ppt' || ext === 'pptx') return '📙';
    if (['jpg', 'jpeg', 'png'].includes(ext)) return '🖼️';
    return '📄';
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '—';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // ── upload submit (real file, sent to server) ──
  const handleUpload = async () => {
    setUploadError('');
    if (!form.title.trim()) { setUploadError('Assignment title is required.'); return; }
    if (!form.subject) { setUploadError('Please select a subject.'); return; }
    if (!form.classLevel) { setUploadError('Please select a class.'); return; }
    if (!uploadedFile) { setUploadError('Please attach a file.'); return; }

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadedFile);
      formData.append('title', form.title.trim());
      formData.append('subject', form.subject);
      formData.append('classLevel', form.classLevel);
      formData.append('dueDate', form.dueDate || '');
      formData.append('description', form.description.trim());
      // Re-send credentials so the backend can verify this upload is authorized
      formData.append('username', loginUser);
      formData.append('password', loginPass);

      const response = await fetch(UPLOAD_API_URL, {
        method: 'POST',
        body: formData,
      });
      const data = await response.json();

      if (data.success) {
        setForm({ title: '', subject: '', classLevel: '', dueDate: '', description: '' });
        setUploadedFile(null);
        setUploadSuccess(`"${form.title}" uploaded successfully!`);
        setTimeout(() => setUploadSuccess(''), 3500);
        fetchAssignments(); // refresh the list so the new one shows up immediately
      } else {
        setUploadError(data.message || 'Upload failed.');
      }
    } catch (err) {
      setUploadError(`Unable to reach the server: ${err.message}`);
    } finally {
      setIsUploading(false);
    }
  };

  // ── delete (real, server-verified) ──
  const confirmDelete = (id) => setDeleteId(id);

  const handleDelete = async () => {
    try {
      const response = await fetch(DELETE_API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: deleteId, username: loginUser, password: loginPass }),
      });
      const data = await response.json();

      if (data.success) {
        setAssignments((prev) => prev.filter((a) => a.id !== deleteId));
      } else {
        setListError(data.message || 'Could not delete this assignment.');
      }
    } catch (err) {
      setListError(`Unable to reach the server: ${err.message}`);
    } finally {
      setDeleteId(null);
    }
  };

  // ── download (real file URL on the server) ──
  const handleDownload = (a) => {
    window.open(a.filepath, '_blank', 'noopener,noreferrer');
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  return (
    <div className="asgn-page">
      <Navbar />

      {/* ── PAGE HERO ── */}
      <section className="asgn-hero">
        <div className="asgn-hero__bg" />
        <div className="asgn-hero__content">
          <span className="asgn-eyebrow">Academic Resources</span>
          <h1>Assignments</h1>
          <p>Download your class assignments, worksheets and study materials uploaded by your teachers.</p>
        </div>
        <button className="asgn-admin-btn" onClick={() => setShowAdminModal(true)}>
          {isLoggedIn ? '⚙️ Admin Panel' : '🔐 Admin Login'}
        </button>
      </section>

      {/* ── FILTERS ── */}
      <section className="asgn-filters">
        <div className="asgn-filters__inner">
          <div className="asgn-search">
            <span className="search-icon">🔍</span>
            <input
              type="text"
              placeholder="Search assignments..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button className="search-clear" onClick={() => setSearchQuery('')}>✕</button>
            )}
          </div>
          <select value={filterClass} onChange={(e) => setFilterClass(e.target.value)}>
            {CLASS_OPTIONS.map((c) => <option key={c}>{c}</option>)}
          </select>
          <select value={filterSubject} onChange={(e) => setFilterSubject(e.target.value)}>
            {SUBJECT_OPTIONS.map((s) => <option key={s}>{s}</option>)}
          </select>
          {(filterClass !== 'All Classes' || filterSubject !== 'All Subjects' || searchQuery) && (
            <button className="filter-reset" onClick={() => { setFilterClass('All Classes'); setFilterSubject('All Subjects'); setSearchQuery(''); }}>
              Reset Filters
            </button>
          )}
        </div>
        <div className="asgn-count">
          {listLoading ? 'Loading...' : `${filtered.length} assignment${filtered.length !== 1 ? 's' : ''} found`}
        </div>
      </section>

      {/* ── ASSIGNMENTS GRID ── */}
      <section className="asgn-grid-section">
        {listError && (
          <div className="asgn-empty">
            <span className="asgn-empty__icon">⚠️</span>
            <h3>Something went wrong</h3>
            <p>{listError}</p>
          </div>
        )}

        {!listError && listLoading && (
          <div className="asgn-empty">
            <span className="asgn-empty__icon">⏳</span>
            <h3>Loading assignments...</h3>
          </div>
        )}

        {!listError && !listLoading && filtered.length === 0 && (
          <div className="asgn-empty">
            <span className="asgn-empty__icon">📂</span>
            <h3>No Assignments Found</h3>
            <p>{assignments.length === 0
              ? 'No assignments have been uploaded yet. Check back soon!'
              : 'No assignments match your current filters.'}</p>
          </div>
        )}

        {!listError && !listLoading && filtered.length > 0 && (
          <div className="asgn-grid">
            {filtered.map((a, i) => (
              <div className="asgn-card" key={a.id} style={{ animationDelay: `${i * 0.06}s` }}>
                <div className="asgn-card__top">
                  <div className="asgn-card__icon">{getFileIconByName(a.filename)}</div>
                  <div className="asgn-card__meta">
                    <span className="asgn-card__class">{a.class_level}</span>
                    <span className="asgn-card__subject">{a.subject}</span>
                  </div>
                  {isLoggedIn && (
                    <button className="asgn-card__delete" onClick={() => confirmDelete(a.id)} title="Delete">🗑️</button>
                  )}
                </div>
                <h3 className="asgn-card__title">{a.title}</h3>
                {a.description && <p className="asgn-card__desc">{a.description}</p>}
                <div className="asgn-card__footer">
                  <div className="asgn-card__info">
                    <span className="asgn-card__file">{a.filename}</span>
                    <span className="asgn-card__size">{formatFileSize(a.file_size)}</span>
                    {a.due_date && (
                      <span className="asgn-card__due">📅 Due: {formatDate(a.due_date)}</span>
                    )}
                    <span className="asgn-card__uploaded">Uploaded: {formatDate(a.uploaded_at)}</span>
                  </div>
                  <button className="asgn-card__download" onClick={() => handleDownload(a)}>
                    ⬇ Download
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* ══════════════════════════════════
          ADMIN MODAL
      ══════════════════════════════════ */}
      {showAdminModal && (
        <div className="modal-overlay" onClick={(e) => { if (e.target.classList.contains('modal-overlay')) { if (!isLoggedIn) setShowAdminModal(false); } }}>
          <div className="modal">

            {/* ── Login Screen ── */}
            {!isLoggedIn ? (
              <div className="modal-login">
                <button className="modal-close" onClick={() => setShowAdminModal(false)}>✕</button>
                <div className="modal-login__icon">🔐</div>
                <h2>Admin Login</h2>
                <p>Sign in to manage assignments</p>
                <div className="form-group">
                  <label>Username</label>
                  <input
                    type="text"
                    placeholder="Enter username"
                    value={loginUser}
                    onChange={(e) => setLoginUser(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
                  />
                </div>
                <div className="form-group">
                  <label>Password</label>
                  <input
                    type="password"
                    placeholder="Enter password"
                    value={loginPass}
                    onChange={(e) => setLoginPass(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
                  />
                </div>
                {loginError && <div className="form-error">{loginError}</div>}
                <button className="modal-login__btn" onClick={handleLogin} disabled={loginLoading}>
                  {loginLoading ? 'Logging in...' : 'Login →'}
                </button>
              </div>
            ) : (
              /* ── Admin Panel ── */
              <div className="modal-admin">
                <div className="modal-admin__header">
                  <div>
                    <h2>Admin Panel</h2>
                    <p>Upload new assignment documents</p>
                  </div>
                  <div className="modal-admin__header-actions">
                    <span className="admin-badge">✅ Logged in as <strong>{loginUser}</strong></span>
                    <button className="btn-logout" onClick={handleLogout}>Logout</button>
                    <button className="modal-close" onClick={() => setShowAdminModal(false)}>✕</button>
                  </div>
                </div>

                <div className="modal-admin__body">
                  {/* Summary bar */}
                  <div className="admin-summary">
                    <div className="admin-summary__card">
                      <span>{assignments.length}</span>
                      <label>Total Assignments</label>
                    </div>
                    <div className="admin-summary__card">
                      <span>{[...new Set(assignments.map((a) => a.class_level))].length}</span>
                      <label>Classes Covered</label>
                    </div>
                    <div className="admin-summary__card">
                      <span>{[...new Set(assignments.map((a) => a.subject))].length}</span>
                      <label>Subjects</label>
                    </div>
                  </div>

                  <h3 className="upload-heading">Upload New Assignment</h3>

                  <div className="upload-form">
                    <div className="form-row">
                      <div className="form-group">
                        <label>Assignment Title *</label>
                        <input
                          type="text"
                          placeholder="e.g. Chapter 5 — Algebra Practice"
                          value={form.title}
                          onChange={(e) => setForm({ ...form, title: e.target.value })}
                        />
                      </div>
                      <div className="form-group">
                        <label>Due Date</label>
                        <input
                          type="date"
                          value={form.dueDate}
                          onChange={(e) => setForm({ ...form, dueDate: e.target.value })}
                        />
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label>Subject *</label>
                        <select value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })}>
                          <option value="">Select Subject</option>
                          {SUBJECT_OPTIONS.filter((s) => s !== 'All Subjects').map((s) => (
                            <option key={s}>{s}</option>
                          ))}
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Class *</label>
                        <select value={form.classLevel} onChange={(e) => setForm({ ...form, classLevel: e.target.value })}>
                          <option value="">Select Class</option>
                          {CLASS_OPTIONS.filter((c) => c !== 'All Classes').map((c) => (
                            <option key={c}>{c}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="form-group">
                      <label>Description (optional)</label>
                      <textarea
                        placeholder="Brief description or instructions for students..."
                        rows={3}
                        value={form.description}
                        onChange={(e) => setForm({ ...form, description: e.target.value })}
                      />
                    </div>

                    {/* Drag & Drop Zone */}
                    <div
                      className={`drop-zone ${isDragging ? 'drop-zone--active' : ''} ${uploadedFile ? 'drop-zone--filled' : ''}`}
                      onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                      onDragLeave={() => setIsDragging(false)}
                      onDrop={handleFileDrop}
                      onClick={() => fileRef.current.click()}
                    >
                      <input
                        type="file"
                        ref={fileRef}
                        style={{ display: 'none' }}
                        accept=".pdf,.doc,.docx,.ppt,.pptx,.jpg,.jpeg,.png"
                        onChange={handleFileInput}
                      />
                      {uploadedFile ? (
                        <div className="drop-zone__file">
                          <span className="drop-zone__file-icon">{getFileIcon(uploadedFile.type)}</span>
                          <div>
                            <strong>{uploadedFile.name}</strong>
                            <span>{formatFileSize(uploadedFile.size)}</span>
                          </div>
                          <button
                            className="drop-zone__remove"
                            onClick={(e) => { e.stopPropagation(); setUploadedFile(null); }}
                          >✕</button>
                        </div>
                      ) : (
                        <>
                          <span className="drop-zone__icon">☁️</span>
                          <p><strong>Drag & drop</strong> or <span className="drop-zone__browse">click to browse</span></p>
                          <small>PDF, DOC, DOCX, PPT, PPTX, JPG, PNG — max 10 MB</small>
                        </>
                      )}
                    </div>

                    {uploadError && <div className="form-error">{uploadError}</div>}
                    {uploadSuccess && <div className="form-success">{uploadSuccess}</div>}

                    <button className="btn-upload" onClick={handleUpload} disabled={isUploading}>
                      {isUploading ? 'Uploading...' : '⬆ Upload Assignment'}
                    </button>
                  </div>

                  {/* Recent uploads list inside panel */}
                  {assignments.length > 0 && (
                    <div className="admin-recent">
                      <h3>Recent Uploads</h3>
                      <div className="admin-recent__list">
                        {assignments.slice(0, 5).map((a) => (
                          <div className="admin-recent__item" key={a.id}>
                            <span className="admin-recent__icon">{getFileIconByName(a.filename)}</span>
                            <div className="admin-recent__info">
                              <strong>{a.title}</strong>
                              <span>{a.class_level} · {a.subject} · {formatDate(a.uploaded_at)}</span>
                            </div>
                            <button className="admin-recent__del" onClick={() => confirmDelete(a.id)}>🗑️</button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Delete Confirm Dialog ── */}
      {deleteId && (
        <div className="modal-overlay">
          <div className="modal modal--confirm">
            <div className="confirm-icon">⚠️</div>
            <h3>Delete Assignment?</h3>
            <p>This action cannot be undone. The file will be permanently removed.</p>
            <div className="confirm-actions">
              <button className="btn-cancel" onClick={() => setDeleteId(null)}>Cancel</button>
              <button className="btn-delete" onClick={handleDelete}>Yes, Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Assignments;